/**
 * What the soundtrack (`audio/music.ts`) plays at a moment of the match: the stage by the match
 * clock (`BALANCE.phases`: weapons free 4:00, capitals exposed 10:00, final 17:00), how tense it
 * is (the stage, the leader beacon from 12:00, warheads coming at us, cities lost) and our own
 * trouble (a warhead on something of ours, our capital critical, knocked out).
 */
import { BALANCE, type PlayerView, type Tick } from '@sa/sim';
import { STAGE, type MusicCue, type MusicStage } from '../audio/music';
import type { InboundThreat } from './threats';

const P = BALANCE.phases;
/** tension before anything happens to us, by stage (lobby … aftermath) */
const BASE_TENSION = [0.1, 0.15, 0.25, 0.4, 0.55, 0.3];
/** the leader beacon (12:00) tightens the capitals-exposed stage */
const BEACON_TENSION = 0.15;
/** warheads at stake (3+: all of it) and cities lost (4+: all of it) */
const THREAT_TENSION = 0.25;
const LOSS_TENSION = 0.2;

export function musicStage(tick: Tick, ended: boolean): MusicStage {
  if (ended) return STAGE.aftermath;
  if (tick >= P.finalTick) return STAGE.final;
  if (tick >= P.capitalsExposedTick) return STAGE.exposed;
  if (tick >= P.releaseTick) return STAGE.release;
  return STAGE.watch;
}

export function soundtrackCue(
  view: Pick<PlayerView, 'me' | 'eliminated' | 'criticalCapitals'>,
  tick: Tick,
  threats: readonly InboundThreat[],
  citiesLost: number,
  ended: boolean,
): MusicCue {
  const stage = musicStage(tick, ended);
  if (ended) return { stage, tension: BASE_TENSION[stage], inbound: false, critical: false, eliminated: false };
  // open ground is a calm inbound (no alarm): only a warhead on something of ours counts
  const atStake = threats.filter((t) => t.stake !== null).length;
  const tension =
    BASE_TENSION[stage] +
    (stage === STAGE.exposed && tick >= P.beaconTick ? BEACON_TENSION : 0) +
    THREAT_TENSION * Math.min(1, atStake / 3) +
    LOSS_TENSION * Math.min(1, citiesLost / 4);
  return {
    stage,
    tension: Math.min(1, tension),
    inbound: !view.eliminated && atStake > 0,
    critical: !view.eliminated && (view.criticalCapitals.get(view.me) ?? -1) > tick,
    eliminated: view.eliminated,
  };
}
